import React, { Component, ErrorInfo } from 'react';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = React.createContext<ThemeContextType | undefined>(undefined);

const getInitialTheme = (): Theme => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const savedTheme = localStorage.getItem('theme');
      return (savedTheme as Theme) || 'light';
    }
  } catch (error) {
    console.warn('Failed to access localStorage for theme:', error);
  }
  return 'dark';
};

// Inner provider that uses hooks normally
const ThemeProviderInner = ({ children }: { children: React.ReactNode }) => {
  const [theme, setTheme] = React.useState<Theme>(getInitialTheme);

  React.useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        const root = window.document.documentElement;
        root.classList.remove('light', 'dark');
        root.classList.add(theme);
        if (window.localStorage) {
          localStorage.setItem('theme', theme);
        }
      }
    } catch (error) {
      console.warn('Failed to update theme:', error);
    }
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Error boundary that falls back to rendering children without theme context
class ThemeBoundary extends Component<{ children: React.ReactNode; fallback: React.ReactNode }, { hasError: boolean }> {
  state = { hasError: false };
  static getDerivedStateFromError(_error: Error) { return { hasError: true }; }
  componentDidCatch(error: Error, info: ErrorInfo) { console.warn('ThemeProvider error caught:', error, info); }
  render() { return this.state.hasError ? this.props.fallback : this.props.children; }
}

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  return (
    <ThemeBoundary fallback={<>{children}</>}>
      <ThemeProviderInner>{children}</ThemeProviderInner>
    </ThemeBoundary>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = React.useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
